/**
 * Payment Split Service
 * Handles validation and processing of split payments across multiple methods
 * Supports: Cash, Bank (Card), Koko, MintPay
 */

/**
 * Validate payment split
 * @param {Object} split - Payment split object { cash: 100, bank: 50, koko: 0, mintpay: 0 }
 * @param {number} totalAmount - Total bill amount
 * @param {Object} billingSettings - Settings for Koko/MintPay charge percentages
 * @returns {Object} { valid: boolean, error: string, totalSplit: number }
 */
export const validatePaymentSplit = (split, totalAmount, billingSettings = {}) => {
  if (!split || typeof split !== 'object') {
    return { valid: false, error: 'Invalid payment split data' };
  }

  // Extract split amounts
  const cash = parseFloat(split.cash) || 0;
  const bank = parseFloat(split.bank) || 0;
  const koko = parseFloat(split.koko) || 0;
  const mintpay = parseFloat(split.mintpay) || 0;

  // Validate individual amounts
  if (cash < 0 || bank < 0 || koko < 0 || mintpay < 0) {
    return { valid: false, error: 'Payment amounts cannot be negative' };
  }

  // At least one payment method must be selected
  const totalSplit = cash + bank + koko + mintpay;
  if (totalSplit === 0) {
    return { valid: false, error: 'Please select at least one payment method' };
  }

  // Total split must equal bill amount (with small tolerance for floating point)
  const tolerance = 0.01;
  if (Math.abs(totalSplit - totalAmount) > tolerance) {
    return {
      valid: false,
      error: `Payment split (${totalSplit.toFixed(2)}) must equal total amount (${totalAmount.toFixed(2)})`,
      totalSplit
    };
  }

  return { valid: true, totalSplit };
};

/**
 * Calculate Koko charge for split payment
 * @param {number} kokoAmount - Amount to be paid via Koko
 * @param {number} kokoChargePercentage - Koko charge percentage (default 12%)
 * @returns {number} Koko charge amount
 */
export const calculateKokoChargeForSplit = (kokoAmount, kokoChargePercentage = 12) => {
  if (kokoAmount <= 0) return 0;
  return (kokoAmount * kokoChargePercentage) / 100;
};

/**
 * Calculate MintPay charge for split payment
 * @param {number} mintPayAmount - Amount to be paid via MintPay
 * @param {number} mintPayChargePercentage - MintPay charge percentage (default 12%)
 * @returns {number} MintPay charge amount
 */
export const calculateMintPayChargeForSplit = (mintPayAmount, mintPayChargePercentage = 12) => {
  if (mintPayAmount <= 0) return 0;
  return (mintPayAmount * mintPayChargePercentage) / 100;
};

/**
 * Get payment methods used in split
 * @param {Object} split - Payment split object
 * @returns {Array} Array of payment methods with amounts
 */
export const getPaymentMethodsUsed = (split) => {
  const methods = [];

  if (split.cash && split.cash > 0) {
    methods.push({ method: 'cash', amount: split.cash, label: 'Cash' });
  }
  if (split.bank && split.bank > 0) {
    methods.push({ method: 'bank', amount: split.bank, label: 'Bank/Card' });
  }
  if (split.koko && split.koko > 0) {
    methods.push({ method: 'koko', amount: split.koko, label: 'Koko' });
  }
  if (split.mintpay && split.mintpay > 0) {
    methods.push({ method: 'mintpay', amount: split.mintpay, label: 'MintPay' });
  }

  return methods;
};

/**
 * Format payment split for display
 * @param {Object} split - Payment split object
 * @param {Object} billingSettings - Settings for Koko charge
 * @returns {string} Formatted string like "Cash: 500 | Bank: 300"
 */
export const formatPaymentSplit = (split, billingSettings = {}) => {
  const methods = getPaymentMethodsUsed(split);
  
  return methods
    .map(m => {
      if (m.method === 'koko') {
        const kokoCharge = calculateKokoChargeForSplit(m.amount, billingSettings.kokoChargePercentage || 12);
        return `${m.label}: ${m.amount.toFixed(2)} (+${kokoCharge.toFixed(2)} charge)`;
      }
      if (m.method === 'mintpay') {
        const mintPayCharge = calculateMintPayChargeForSplit(m.amount, billingSettings.mintPayChargePercentage || 12);
        return `${m.label}: ${m.amount.toFixed(2)} (+${mintPayCharge.toFixed(2)} charge)`;
      }
      return `${m.label}: ${m.amount.toFixed(2)}`;
    })
    .join(' | ');
};

/**
 * Create ledger entries for split payment
 * Used to generate the data needed for cash/bank ledger entries
 * @param {Object} split - Payment split object
 * @param {string} billId - Bill ID for reference
 * @param {string} invoiceNumber - Invoice number
 * @param {string} customerName - Customer name
 * @param {Object} billingSettings - Settings for Koko charge
 * @returns {Array} Array of ledger entry objects
 */
export const createLedgerEntriesForSplit = (split, billId, invoiceNumber, customerName, billingSettings = {}) => {
  const entries = [];
  const methods = getPaymentMethodsUsed(split);

  methods.forEach(m => {
    if (m.method === 'cash') {
      entries.push({
        accountType: 'petty_cash',
        amount: m.amount,
        paymentMethod: 'cash',
        description: `Sale - ${invoiceNumber} - ${customerName}`,
        referenceId: billId,
        referenceType: 'sale'
      });
    } else if (m.method === 'bank') {
      entries.push({
        accountType: 'cash_at_bank',
        amount: m.amount,
        paymentMethod: 'bank',
        description: `Sale - ${invoiceNumber} - ${customerName}`,
        referenceId: billId,
        referenceType: 'sale'
      });
    } else if (m.method === 'koko') {
      const kokoCharge = calculateKokoChargeForSplit(m.amount, billingSettings.kokoChargePercentage || 12);
      entries.push({
        accountType: 'cash_at_bank',
        amount: m.amount + kokoCharge,
        paymentMethod: 'koko',
        description: `Sale (Koko) - ${invoiceNumber} - ${customerName}`,
        referenceId: billId,
        referenceType: 'sale',
        kokoCharge
      });
    } else if (m.method === 'mintpay') {
      const mintPayCharge = calculateMintPayChargeForSplit(m.amount, billingSettings.mintPayChargePercentage || 12);
      entries.push({
        accountType: 'cash_at_bank',
        amount: m.amount + mintPayCharge,
        paymentMethod: 'mintpay',
        description: `Sale (MintPay) - ${invoiceNumber} - ${customerName}`,
        referenceId: billId,
        referenceType: 'sale',
        mintPayCharge
      });
    }
  });

  return entries;
};

/**
 * Check if payment split is enabled (more than one method selected)
 * @param {Object} split - Payment split object
 * @returns {boolean}
 */
export const isPaymentSplitEnabled = (split) => {
  const methods = getPaymentMethodsUsed(split);
  return methods.length > 1;
};

/**
 * Get default payment split (all to single method)
 * @param {string} paymentType - Payment type (Cash, Bank, Card, Koko, MintPay)
 * @returns {Object} Default split object
 */
export const getDefaultPaymentSplit = (paymentType = 'Cash') => {
  const split = {
    cash: 0,
    bank: 0,
    koko: 0,
    mintpay: 0
  };

  // Map old paymentType to new split format
  if (paymentType === 'Cash') {
    split.cash = 1; // Will be set to actual amount in component
  } else if (paymentType === 'Card' || paymentType === 'Bank') {
    split.bank = 1;
  } else if (paymentType === 'Koko') {
    split.koko = 1;
  } else if (paymentType === 'MintPay') {
    split.mintpay = 1;
  }

  return split;
};
